import React, { useMemo, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import { Alert, Box, CircularProgress, Stack, TextField, Typography } from '@mui/material';

import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import { useFlashHotelCatalog, useUpdateCatalogConfig } from '~/hooks/apis/rewards';

import type { FlashHotelCatalogConfig } from '~/services/LuxLoyalty';

import type { FlashHotelCatalog } from './validateFlashConfig';
import { FLASH_CONFIG_FIELD_HELPER_TEXT, flashConfigState, validateFlashConfig } from './validateFlashConfig';

/** Field order the form renders in. */
const FIELD_ORDER: ReadonlyArray<keyof FlashHotelCatalogConfig> = [
  'copayPercentage',
  'pointsCoveragePercentage',
  'burnRateAmplifier',
  'minMarginPercent',
  'pointsRounding',
  'vppFloorCents',
  'vppCeilingCents',
  'defaultAllocation',
  'minRoomsRemaining',
];

/** Display label for each Flash Config field. */
const FIELD_LABELS: Record<keyof FlashHotelCatalogConfig, string> = {
  copayPercentage: 'Co-pay percentage',
  pointsCoveragePercentage: 'Points coverage percentage',
  burnRateAmplifier: 'Burn rate amplifier',
  minMarginPercent: 'Minimum margin percent',
  pointsRounding: 'Points rounding',
  vppFloorCents: 'VPP floor (cents)',
  vppCeilingCents: 'VPP ceiling (cents)',
  defaultAllocation: 'Default allocation',
  minRoomsRemaining: 'Minimum rooms remaining',
};

/**
 * Extra context appended to a field's bound-range helper text, for fields whose meaning isn't
 * obvious from the label + range alone.
 */
const FIELD_HELPER_EXTRA: Partial<Record<keyof FlashHotelCatalogConfig, string>> = {
  burnRateAmplifier: 'Points earned = burn amount ÷ amplifier.',
  vppFloorCents: 'Minimum acceptable value-per-point. Must not exceed the ceiling.',
  vppCeilingCents: 'Maximum acceptable value-per-point. Must be at least the floor.',
};

/** Full helper text for a field: its bound range, plus any extra context from `FIELD_HELPER_EXTRA`. */
function fieldHelperText(field: keyof FlashHotelCatalogConfig): string {
  const extra = FIELD_HELPER_EXTRA[field];
  return extra ? `${FLASH_CONFIG_FIELD_HELPER_TEXT[field]}. ${extra}` : FLASH_CONFIG_FIELD_HELPER_TEXT[field];
}

/** Exact confirmation copy required before a config save is submitted. */
const CONFIRM_SAVE_DESCRIPTION =
  'This changes every future price computation — the formula version will change and the nightly sweep will restamp/reprice packages.';

/** Converts a loaded config into the form's string state. */
function toFormValues(config: FlashHotelCatalogConfig): Record<keyof FlashHotelCatalogConfig, string> {
  return FIELD_ORDER.reduce(
    (acc, field) => {
      acc[field] = String(config[field]);
      return acc;
    },
    {} as Record<keyof FlashHotelCatalogConfig, string>,
  );
}

/**
 * Admin "Flash Config" tab: edits the hotel_flash reward catalog's 9 pricing/display constants
 * (co-pay %, points coverage %, burn rate amplifier, minimum margin %, points rounding, VPP floor
 * and ceiling in cents, default allocation, minimum rooms remaining).
 *
 * Fields are string-backed and re-validated on every keystroke via `validateFlashConfig` —
 * including the VPP floor/ceiling cross-field rule — and Save stays disabled until the form has
 * been touched and every field is valid. Because the API replaces all 9 constants in one PATCH
 * (and doing so bumps the catalog's `formulaVersion`, triggering a full nightly re-sweep of every
 * flash-hotel package), Save always confirms first with the exact required warning copy before
 * submitting.
 *
 * The form only ever shows values loaded from loyalty. When the config can't be loaded, or comes
 * back missing or invalid, the page shows an error and no form, rather than offering made-up
 * values that a save would then write back.
 */
export default function FlashRewardsConfigPage() {
  const catalogQuery = useFlashHotelCatalog();
  const updateMutation = useUpdateCatalogConfig();
  const showConfirmDialog = useConfirmDialog();

  const catalog = catalogQuery.data as FlashHotelCatalog | null | undefined;
  const configState = flashConfigState(catalogQuery);
  const loadedConfig = configState.status === 'ready' ? configState.config : null;

  // `edits` holds the admin's in-progress values and is null until they type. Until then the form
  // shows the loaded config, so a refetch (e.g. after a save) is reflected straight away and an
  // in-progress edit is never clobbered by one.
  const [edits, setEdits] = useState<Record<keyof FlashHotelCatalogConfig, string> | null>(null);
  const [formulaVersion, setFormulaVersion] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const values = useMemo(() => edits ?? (loadedConfig ? toFormValues(loadedConfig) : null), [edits, loadedConfig]);
  const { config, errors } = useMemo(
    () => (values ? validateFlashConfig(values) : { config: undefined, errors: {} }),
    [values],
  );
  const touched = edits !== null;

  const setField = (field: keyof FlashHotelCatalogConfig, value: string) => {
    if (!values) return;
    setFormulaVersion(null);
    setSubmitError(null);
    setEdits({ ...values, [field]: value });
  };

  const handleSave = async () => {
    if (!config || !catalog) return;

    const confirmed = await showConfirmDialog({
      title: 'Update flash pricing config?',
      description: CONFIRM_SAVE_DESCRIPTION,
    });
    if (!confirmed) return;

    setSubmitError(null);
    try {
      const result = await updateMutation.mutateAsync({ catalogId: catalog.id, config });
      setFormulaVersion(result.formulaVersion);
      setEdits(null);
    } catch (error) {
      // Form values are deliberately kept on error — the admin's input isn't lost on a failed save.
      setSubmitError(error instanceof Error ? error.message : 'Failed to update the flash config.');
    }
  };

  const saveDisabled = !touched || !catalog || Object.keys(errors).length > 0 || updateMutation.isPending;

  return (
    <Stack spacing={3} sx={{ py: 3 }}>
      <Typography variant="h5">Flash Config</Typography>
      <Typography variant="body2" color="text.secondary">
        Pricing and display constants for the hotel_flash reward catalog. Saving replaces all 9 values at once and bumps
        the formula version, so the nightly sweep restamps and reprices every flash-hotel package.
      </Typography>

      {configState.status === 'loading' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {configState.status === 'error' && <Alert severity="error">{configState.message}</Alert>}

      {catalog && values && (
        <Stack spacing={3}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
            {FIELD_ORDER.map((field) => (
              <TextField
                key={field}
                label={FIELD_LABELS[field]}
                value={values[field]}
                onChange={(e) => setField(field, e.target.value)}
                error={Boolean(errors[field])}
                helperText={errors[field] ?? fieldHelperText(field)}
                fullWidth
              />
            ))}
          </Box>

          {submitError && <Alert severity="error">{submitError}</Alert>}
          {formulaVersion && (
            <Alert severity="success">Flash config updated — new formula version: {formulaVersion}</Alert>
          )}

          <Box>
            <LoadingButton
              variant="contained"
              onClick={handleSave}
              loading={updateMutation.isPending}
              disabled={saveDisabled}
            >
              Save
            </LoadingButton>
          </Box>
        </Stack>
      )}
    </Stack>
  );
}
